import { Alert, Anchor, Badge, Button, Group, Paper, Stack, Text, TextInput, ThemeIcon } from "@mantine/core"
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  IconArrowLeft,
  IconChecks,
  IconCreditCard,
  IconHeadset,
  IconLock,
  IconReceipt2,
  IconShieldCheck,
  appIcon,
} from "atlas_forecasting_app/components/common/icons"
import { ErrorAlert, LoadingState, PageHeader } from "atlas_forecasting_app/components/common/page_elements"
import { subscribeToBalanceTopUpStatus } from "atlas_forecasting_app/lib/cable"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatCurrencyFromCents, formatSignedCurrencyFromCents } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"

const TERMINAL_TOP_UP_STATUSES = new Set(["credited", "partially_refunded", "refunded", "disputed", "failed", "expired"])
const STEP_ITEMS = [
  "Choose amount",
  "Secure Stripe checkout",
  "Balance updates automatically",
]
const AMOUNT_FORMAT = /^\d+(?:\.\d{1,2})?$/
const TRUST_POINTS = [
  {
    icon: IconShieldCheck,
    title: "Powered by Stripe",
    body: "Payments are processed by Stripe.",
  },
  {
    icon: IconLock,
    title: "Card details stay with Stripe",
    body: "Atlas does not store your full card number.",
  },
  {
    icon: IconChecks,
    title: "Balance updates automatically",
    body: "Atlas credits your balance after payment confirmation.",
  },
]

function timeMs(value) {
  const parsed = value ? Date.parse(value) : Number.NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

function newestTopUp(currentTopUp, nextTopUp) {
  if (!nextTopUp) return currentTopUp
  if (!currentTopUp) return nextTopUp
  return timeMs(nextTopUp.updated_at) >= timeMs(currentTopUp.updated_at) ? { ...currentTopUp, ...nextTopUp } : currentTopUp
}

function statusSummary(topUp) {
  if (!topUp) return { title: "Checking payment...", body: "Waiting for Stripe to confirm the status of this payment." }

  switch (topUp.status) {
    case "credited":
      return {
        title: "Funds added",
        body: `Your Atlas balance was credited by ${formatCurrencyFromCents(topUp.net_wallet_cents)}.`,
        color: "green",
      }
    case "partially_refunded":
      return {
        title: "Funds credited with later refund activity",
        body: `Net wallet impact: ${formatSignedCurrencyFromCents(topUp.net_wallet_cents)}.`,
        color: "yellow",
      }
    case "refunded":
      return {
        title: "Top-up refunded",
        body: "This payment has been refunded and the credited funds were reversed from your Atlas balance.",
        color: "yellow",
      }
    case "disputed":
      return {
        title: "Top-up disputed",
        body: "This payment is in dispute. Funds may have been withdrawn from your Atlas balance until Stripe resolves the case.",
        color: "red",
      }
    case "failed":
      return {
        title: "Payment failed",
        body: "Stripe did not complete this payment. You can start a new add-funds flow.",
        color: "red",
      }
    case "expired":
      return {
        title: "Payment expired",
        body: "This Checkout Session expired before payment completed. Start a new add-funds flow to try again.",
        color: "yellow",
      }
    case "processing":
      return {
        title: "Processing payment",
        body: "Stripe reports the payment is processing. Atlas will credit your balance once the webhook confirms it.",
        color: "blue",
      }
    default:
      return {
        title: "Waiting for confirmation",
        body: "Atlas is waiting for Stripe's final confirmation before updating your balance.",
        color: "blue",
      }
  }
}

function parseDollarAmountToCents(value) {
  const normalized = value?.toString().trim() || ""
  if (!normalized || !AMOUNT_FORMAT.test(normalized)) return null

  return Math.round(Number(normalized) * 100)
}

function amountErrorMessage(amountCents, stripeConfig) {
  if (amountCents == null) return "Enter a dollar amount to continue."
  if (amountCents < stripeConfig.min_add_funds_cents) return `Minimum top-up is ${formatCurrencyFromCents(stripeConfig.min_add_funds_cents)}.`
  if (amountCents > stripeConfig.max_add_funds_cents) return `Maximum top-up is ${formatCurrencyFromCents(stripeConfig.max_add_funds_cents)}.`

  return ""
}

function topUpStatusLabel(status) {
  if (!status) return "Awaiting confirmation"

  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function PaymentSteps({ activeStep = 1 }) {
  return (
    <div className="af-payment-steps" aria-label="Payment steps">
      {STEP_ITEMS.map((label, index) => {
        const stepNumber = index + 1
        const isActive = stepNumber <= activeStep

        return (
          <div key={label} className={`af-payment-step ${isActive ? "is-active" : ""}`}>
            <span className="af-payment-step-number">{stepNumber}</span>
            <span className="af-payment-step-label">{label}</span>
          </div>
        )
      })}
    </div>
  )
}

function PresetAmountOption({ amountCents, selected = false, onSelect }) {
  return (
    <button
      type="button"
      className={`af-payment-selection-card af-payment-amount-option ${selected ? "is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {selected ? (
        <span className="af-payment-selection-card__indicator" aria-hidden="true">
          {appIcon(IconChecks, "inline")}
        </span>
      ) : null}
      <span className="af-payment-amount-option__content">
        <span className="af-payment-amount-option__amount">{formatCurrencyFromCents(amountCents)}</span>
        <span className="af-payment-amount-option__caption">Add to wallet</span>
      </span>
    </button>
  )
}

function SummaryRow({ label, value, emphasize = false }) {
  return (
    <div className={`af-payment-summary-row ${emphasize ? "is-emphasized" : ""}`}>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size={emphasize ? "lg" : "sm"} fw={emphasize ? 700 : 600}>
        {value}
      </Text>
    </div>
  )
}

function TrustPanel({ clientSecret }) {
  return (
    <Paper withBorder radius="xl" p="xl" className="af-payment-trust-card">
      <Stack gap="lg">
        <div className="af-payment-trust-hero">
          <Badge variant="light" color="blue" size="lg">
            Powered by Stripe
          </Badge>
          <Text className="af-payment-trust-title">Secure checkout, clear balance updates, and support if anything looks off.</Text>
          <Text c="dimmed" className="af-payment-trust-copy">
            Atlas uses Stripe for payment processing and updates your wallet after the payment confirmation arrives.
          </Text>
        </div>

        <Stack gap="md">
          {TRUST_POINTS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="af-payment-trust-point">
              <ThemeIcon variant="light" color="blue" radius="xl" size={42}>
                {appIcon(Icon, "page")}
              </ThemeIcon>
              <div className="af-payment-trust-point-body">
                <Text fw={600}>{title}</Text>
                <Text size="sm" c="dimmed">
                  {body}
                </Text>
              </div>
            </div>
          ))}
        </Stack>

        <Paper radius="lg" p="md" className="af-payment-trust-support">
          <Group justify="space-between" align="flex-start" gap="sm">
            <Group gap="xs" align="center">
              <ThemeIcon variant="light" color="gray" radius="xl" size={34}>
                {appIcon(IconHeadset, "inline")}
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">
                  Billing questions?
                </Text>
                <Text size="sm" c="dimmed">
                  Reach the Atlas team if you need help with a payment.
                </Text>
              </div>
            </Group>
            <Anchor component={RouterLink} to="/contact" size="sm" fw={600}>
              Contact support
            </Anchor>
          </Group>
        </Paper>

        {clientSecret ? (
          <Alert color="blue" variant="light" radius="lg">
            The secure checkout is ready below. Closing this page early will not lose the payment, but your balance may update a few seconds later.
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  )
}

export function AddFundsPage({ stripeConfig, currentBalanceCents = 0, onCurrentUserUpdate }) {
  const [amount, setAmount] = useState((stripeConfig?.add_funds_presets_cents?.[0] ?? 0) / 100)
  const [customAmount, setCustomAmount] = useState("")
  const [usingCustomAmount, setUsingCustomAmount] = useState(false)
  const [creating, setCreating] = useState(false)
  const [clientSecret, setClientSecret] = useState("")
  const [error, setError] = useState("")
  const customAmountInputRef = useRef(null)

  const selectedAmount = usingCustomAmount ? customAmount : (amount || "").toString()
  const selectedAmountCents = parseDollarAmountToCents(selectedAmount)
  const amountError = amountErrorMessage(selectedAmountCents, stripeConfig || {})
  const amountReady = !amountError
  const topUpAmountLabel = selectedAmountCents == null ? "Enter an amount" : formatCurrencyFromCents(selectedAmountCents)
  const projectedBalanceLabel = amountReady ? formatCurrencyFromCents(currentBalanceCents + selectedAmountCents) : "—"
  const stripePromise = useMemo(
    () => (clientSecret && stripeConfig?.publishable_key ? loadStripe(stripeConfig.publishable_key) : null),
    [clientSecret, stripeConfig?.publishable_key],
  )
  const checkoutOptions = useMemo(
    () => ({ fetchClientSecret: () => Promise.resolve(clientSecret) }),
    [clientSecret],
  )

  const createCheckoutSession = async () => {
    setCreating(true)
    try {
      const data = await apiFetch("/api/me/balance_top_ups", {
        method: "POST",
        body: { amount: selectedAmount },
      })
      setClientSecret(data?.checkout?.client_secret || "")
      if (data?.user) onCurrentUserUpdate?.(data.user)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setCreating(false)
    }
  }

  if (!stripeConfig?.enabled) {
    return (
      <Stack gap="lg">
        <PageHeader
          eyebrow="Account"
          variant="account"
          title="Add funds"
          subtitle="Stripe is not configured for this environment."
          action={
            <Button component={RouterLink} to="/account/balance-history" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
              Back to balance history
            </Button>
          }
        />
      </Stack>
    )
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Account"
        variant="account"
        title="Add funds"
        subtitle="Securely add funds to your Atlas balance"
        action={
          <Button component={RouterLink} to="/account/balance-history" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Back to balance history
          </Button>
        }
      />

      <ErrorAlert message={error} />

      <div className="af-payment-layout">
        <Paper withBorder radius="xl" p="xl" className="af-payment-main-card">
          <Stack gap="xl">
            <PaymentSteps activeStep={clientSecret ? 2 : 1} />

            {!clientSecret ? (
              <>
                <div className="af-payment-card-heading">
                  <div>
                    <Text className="af-payment-eyebrow">Step 1 of 3</Text>
                    <Text className="af-payment-heading">Choose your top-up amount</Text>
                    <Text c="dimmed">Start with a quick wallet top-up, then finish payment in Stripe Checkout.</Text>
                  </div>
                </div>

                <div className="af-payment-amount-grid">
                  {(stripeConfig?.add_funds_presets_cents || []).map((presetCents) => (
                    <PresetAmountOption
                      key={presetCents}
                      amountCents={presetCents}
                      selected={!usingCustomAmount && amount === presetCents / 100}
                      onSelect={() => {
                        setAmount(presetCents / 100)
                        setUsingCustomAmount(false)
                        setCustomAmount("")
                      }}
                    />
                  ))}

                  <Paper
                    withBorder
                    radius="lg"
                    p="md"
                    className={`af-payment-selection-card af-payment-custom-amount ${usingCustomAmount ? "is-selected" : ""}`}
                    data-selected={usingCustomAmount ? "true" : "false"}
                    onClick={() => {
                      setUsingCustomAmount(true)
                      customAmountInputRef.current?.focus()
                    }}
                  >
                    {usingCustomAmount ? (
                      <span className="af-payment-selection-card__indicator" aria-hidden="true">
                        {appIcon(IconChecks, "inline")}
                      </span>
                    ) : null}
                    <Stack gap="xs">
                      <div>
                        <Text size="sm" fw={600}>
                          Custom amount
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatCurrencyFromCents(stripeConfig.min_add_funds_cents)} to {formatCurrencyFromCents(stripeConfig.max_add_funds_cents)}
                        </Text>
                      </div>
                      <TextInput
                        ref={customAmountInputRef}
                        aria-label="Custom amount"
                        value={customAmount}
                        placeholder="e.g. 24.00"
                        leftSection="$"
                        onFocus={() => setUsingCustomAmount(true)}
                        onChange={(event) => {
                          setUsingCustomAmount(true)
                          setCustomAmount(event.currentTarget.value)
                        }}
                      />
                    </Stack>
                  </Paper>
                </div>
              </>
            ) : (
              <>
                <div className="af-payment-card-heading">
                  <div>
                    <Text className="af-payment-eyebrow">Step 2 of 3</Text>
                    <Text className="af-payment-heading">Secure Stripe checkout</Text>
                    <Text c="dimmed">Review the amount below, then complete the payment in the embedded Stripe form.</Text>
                  </div>
                  <Badge variant="light" color="green" size="lg">
                    Secure payment
                  </Badge>
                </div>

                <Alert color="yellow" variant="light" radius="lg">
                  Atlas credits funds only after Stripe's webhook confirms the payment.
                </Alert>
              </>
            )}

            <Paper withBorder radius="xl" p="lg" className="af-payment-summary-card">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs" align="center">
                    <ThemeIcon radius="xl" size={40} variant="light" color="blue">
                      {appIcon(IconReceipt2, "page")}
                    </ThemeIcon>
                    <div>
                      <Text fw={700}>Payment summary</Text>
                      <Text size="sm" c="dimmed">
                        Review the wallet impact before you continue.
                      </Text>
                    </div>
                  </Group>
                </Group>

                <div className="af-payment-summary-rows">
                  <SummaryRow label="Top-up amount" value={topUpAmountLabel} emphasize />
                  <SummaryRow label="Current balance" value={formatCurrencyFromCents(currentBalanceCents)} />
                  <SummaryRow label="Balance after top-up" value={projectedBalanceLabel} />
                </div>

                {!clientSecret ? (
                  <>
                    <Text size="sm" c={amountReady ? "dimmed" : "red"}>
                      {amountReady ? "Your card details will be entered securely in Stripe on the next step." : amountError}
                    </Text>
                    <Button
                      type="button"
                      size="md"
                      disabled={creating || !amountReady || !!clientSecret}
                      onClick={createCheckoutSession}
                      leftSection={appIcon(IconCreditCard, "inline")}
                    >
                      {creating ? "Creating secure session..." : "Continue to secure Stripe checkout"}
                    </Button>
                  </>
                ) : null}
              </Stack>
            </Paper>

            {clientSecret && stripePromise ? (
              <div className="af-payment-checkout-wrap">
                <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
                  <EmbeddedCheckout className="af-payment-embedded-checkout" />
                </EmbeddedCheckoutProvider>
              </div>
            ) : null}
          </Stack>
        </Paper>

        <TrustPanel clientSecret={clientSecret} />
      </div>
    </Stack>
  )
}

export function AddFundsReturnPage({ currentBalanceCents = 0, onCurrentUserUpdate }) {
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const topUpId = searchParams.get("top_up_id")
  const sessionId = searchParams.get("session_id")
  const onCurrentUserUpdateRef = useRef(onCurrentUserUpdate)
  const [topUp, setTopUp] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  onCurrentUserUpdateRef.current = onCurrentUserUpdate

  useEffect(() => {
    if (!topUpId || TERMINAL_TOP_UP_STATUSES.has(topUp?.status)) return undefined

    return subscribeToBalanceTopUpStatus(topUpId, {
      received(data) {
        if (data?.top_up) {
          setTopUp((prev) => newestTopUp(prev, data.top_up))
        }
        if (data?.user) onCurrentUserUpdateRef.current?.(data.user)
        setError("")
        setLoading(false)
      },
    })
  }, [topUp?.status, topUpId])

  useEffect(() => {
    let cancelled = false

    const refresh = async () => {
      if (!topUpId) {
        setError("Missing top-up ID.")
        setLoading(false)
        return
      }

      try {
        const query = new URLSearchParams()
        if (sessionId) query.set("session_id", sessionId)
        const suffix = query.toString() ? `?${query.toString()}` : ""
        const data = await apiFetch(`/api/me/balance_top_ups/${topUpId}${suffix}`)
        if (cancelled) return

        setTopUp((prev) => newestTopUp(prev, data?.top_up || null))
        if (data?.user) onCurrentUserUpdateRef.current?.(data.user)
        setError("")
        setLoading(false)
      } catch (e) {
        if (cancelled) return
        setError(e.message)
        setLoading(false)
      }
    }

    refresh()

    return () => {
      cancelled = true
    }
  }, [sessionId, topUpId])

  const summary = statusSummary(topUp)
  const statusColor = summary.color || "blue"
  const paymentStatusLabel = topUpStatusLabel(topUp?.status)
  const requestedAmountLabel = topUp ? formatCurrencyFromCents(topUp.amount_cents) : "—"
  const walletImpactLabel = topUp ? formatSignedCurrencyFromCents(topUp.net_wallet_cents) : "—"

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Account"
        variant="account"
        title="Add funds"
        subtitle="Securely add funds to your Atlas balance"
        action={
          <Button component={RouterLink} to="/account/balance-history" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Balance history
          </Button>
        }
      />

      <ErrorAlert message={error} />

      <div className="af-payment-layout">
        <Paper withBorder radius="xl" p="xl" className="af-payment-main-card">
          <Stack gap="xl">
            <PaymentSteps activeStep={3} />

            <div className="af-payment-card-heading">
              <div>
                <Text className="af-payment-eyebrow">Step 3 of 3</Text>
                <Text className="af-payment-heading">{summary.title}</Text>
                <Text c="dimmed" className="af-payment-status-copy">
                  {summary.body}
                </Text>
              </div>
              <Badge variant="light" color={statusColor} size="lg">
                {loading ? "Checking payment" : paymentStatusLabel}
              </Badge>
            </div>

            {loading ? (
              <Alert color="blue" variant="light" radius="lg">
                <LoadingState label="Checking payment status..." />
              </Alert>
            ) : null}

            <Paper withBorder radius="xl" p="lg" className="af-payment-summary-card">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs" align="center">
                    <ThemeIcon radius="xl" size={40} variant="light" color={statusColor}>
                      {appIcon(IconReceipt2, "page")}
                    </ThemeIcon>
                    <div>
                      <Text fw={700}>Payment summary</Text>
                      <Text size="sm" c="dimmed">
                        Review the result of this wallet top-up.
                      </Text>
                    </div>
                  </Group>
                </Group>

                <div className="af-payment-summary-rows">
                  <SummaryRow label="Requested amount" value={requestedAmountLabel} emphasize />
                  <SummaryRow label="Net wallet impact so far" value={walletImpactLabel} />
                  <SummaryRow label="Current balance" value={formatCurrencyFromCents(currentBalanceCents)} />
                  <SummaryRow label="Payment status" value={paymentStatusLabel} />
                </div>
              </Stack>
            </Paper>

            <div className="af-payment-action-row">
              <Button component={RouterLink} to="/account/balance-history" leftSection={appIcon(IconArrowLeft, "inline")}>
                Back to balance history
              </Button>
              <Button component={RouterLink} to="/account/add-funds" variant="light" leftSection={appIcon(IconCreditCard, "inline")}>
                Start a new payment
              </Button>
            </div>
          </Stack>
        </Paper>

        <TrustPanel />
      </div>
    </Stack>
  )
}
